import { useState, useEffect, FormEvent, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useAuthStore } from '../hooks/useAuth';
import { useCarrierRequestSlots } from '../hooks/useCarrierRequestSlots';
import { Link } from 'wouter';
import { api, API_BASE_URL } from '../lib/api';
import { validatePhone } from '../utils/fieldValidation';
import { formatDateInTimeZone } from '../utils/timezoneDisplay';

interface POValidationResult {
    valid: boolean;
    po?: {
        id: string;
        number: string;
        client: string;
        skids: number;
        weight: number;
    };
    warehouse?: {
        id: string;
        name: string;
        publicBookingId: string;
        address: string;
        phone: string;
        graceMinutes: number;
        timeZone?: string;
        schedule?: {
            operatingDays: number[];
            openTime: string;
            closeTime: string;
            slotMinutes: number;
        };
    };
    availableDates?: string[];
    availableDateOptions?: { value: string; label: string }[];
    availableTimeSlots?: any[];
    load?: {
        id: string;
        loadNumber: string;
        totalLinkedPOs?: number;
        openLinkedPOs?: number;
        excludedPOs?: number;
    };
    linkedPOs?: { id: string; number: string; client: string; skids: number; weight: number }[];
    error?: string;
}

const firstNonBlank = (...values: Array<string | null | undefined>): string => {
    for (const val of values) {
        if (typeof val === 'string' && val.trim().length > 0) {
            return val.trim();
        }
    }
    return '';
};

export function CarrierRequest() {
    const { user } = useAuthStore();
    const [step, setStep] = useState<number>(() => {
        if (typeof window !== 'undefined') {
            try {
                const hasStep1Draft = localStorage.getItem('baywhiz_carrier_request_draft_v1');
                const raw = sessionStorage.getItem('baywhiz_carrier_step2_session_v1');
                if (hasStep1Draft && raw) {
                    const parsed = JSON.parse(raw);
                    if (parsed?.step === 2 && parsed?.validationResult) {
                        return 2;
                    }
                } else if (!hasStep1Draft && raw) {
                    sessionStorage.removeItem('baywhiz_carrier_step2_session_v1');
                }
            } catch {
                // Ignore
            }
        }
        return 1;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [equipmentExpanded, setEquipmentExpanded] = useState(false);
    const submittingRef = useRef(false);
    const editedFieldsRef = useRef<{
        carrierEmail?: boolean;
        mcNumber?: boolean;
        carrierPhone?: boolean;
        carrierName?: boolean;
    }>({});

    // Style for whitespace visibility in dropdowns (fixes white-on-white issue)
    const optionStyle: CSSProperties = { color: '#0f172a', backgroundColor: '#ffffff' };

    // Resolve non-blank carrier defaults strictly scoped to the currently authenticated carrier account.
    // Unauthenticated / logged-out sessions on a shared browser never read or write localStorage.
    const activeCarrierEmail = user?.role === 'carrier' && user?.email ? user.email.trim().toLowerCase() : '';
    const activeCarrierKey = user?.role === 'carrier'
        ? `${user.id || ''}|${activeCarrierEmail}|${(user.mcNumber || '').trim().toUpperCase()}`
        : 'anonymous';
    const prevCarrierKeyRef = useRef(activeCarrierKey);

    const getCarrierDefaults = () => {
        if (user?.role !== 'carrier' || !activeCarrierEmail) {
            return {
                carrierEmail: '',
                mcNumber: '',
                carrierPhone: '',
                carrierName: '',
            };
        }
        let profile: { email?: string; mcNumber?: string; phone?: string; carrierName?: string } | null = null;
        if (typeof window !== 'undefined') {
            const storageKey = `baywhiz_carrier_profile_v1:${activeCarrierEmail}`;
            const savedProfile = localStorage.getItem(storageKey);
            if (savedProfile) {
                try {
                    const parsed = JSON.parse(savedProfile);
                    const parsedEmail = typeof parsed?.email === 'string' ? parsed.email.trim().toLowerCase() : '';
                    const parsedMcDigits = typeof parsed?.mcNumber === 'string' ? parsed.mcNumber.replace(/[^0-9]/g, '') : '';
                    const userMcDigits = (user?.mcNumber || '').replace(/[^0-9]/g, '');
                    const emailMatches = !parsedEmail || parsedEmail === activeCarrierEmail;
                    const mcMatches = !parsedMcDigits || !userMcDigits || parsedMcDigits === userMcDigits;
                    if (emailMatches && mcMatches) {
                        profile = parsed;
                    }
                } catch (e) {
                    console.error('Failed to parse carrier profile', e);
                }
            }
        }
        return {
            carrierEmail: firstNonBlank(user?.email, profile?.email),
            mcNumber: firstNonBlank(user?.mcNumber, profile?.mcNumber),
            carrierPhone: firstNonBlank(user?.phone, profile?.phone),
            carrierName: firstNonBlank(user?.companyName, profile?.carrierName),
        };
    };

    const initialDefaults = getCarrierDefaults();

    const getSavedStep2Session = () => {
        if (typeof window === 'undefined') return null;
        try {
            const hasStep1Draft = localStorage.getItem('baywhiz_carrier_request_draft_v1');
            const raw = sessionStorage.getItem('baywhiz_carrier_step2_session_v1');
            if (!hasStep1Draft || !raw) return null;
            const parsed = JSON.parse(raw);
            if (parsed && parsed.step === 2 && parsed.validationResult) {
                if (parsed.carrierKey && parsed.carrierKey !== activeCarrierKey && parsed.carrierKey !== 'anonymous') {
                    return null;
                }
                return parsed;
            }
        } catch {
            // Ignore session storage errors
        }
        return null;
    };

    const initialStep2Session = getSavedStep2Session();

    // Read optional URL query params (?warehouse= / ?publicBookingId= / ?po= / ?poNumber=) or saved Step 1/2 draft
    const getInitialPOValidation = () => {
        if (initialStep2Session?.poValidation) {
            return {
                poNumber: initialStep2Session.poValidation.poNumber || '',
                warehouseBookingId: initialStep2Session.poValidation.warehouseBookingId || '',
                carrierEmail: firstNonBlank(initialStep2Session.poValidation.carrierEmail, initialDefaults.carrierEmail),
            };
        }
        let queryWh = '';
        let queryPo = '';
        let savedDraft: { poNumber?: string; warehouseBookingId?: string; carrierEmail?: string; carrierKey?: string } | null = null;
        if (typeof window !== 'undefined') {
            try {
                const params = new URLSearchParams(window.location.search);
                queryWh = (params.get('warehouse') || params.get('publicBookingId') || params.get('warehouseBookingId') || '').trim();
                queryPo = (params.get('po') || params.get('poNumber') || '').trim();
                const rawDraft = localStorage.getItem('baywhiz_carrier_request_draft_v1');
                if (rawDraft) {
                    savedDraft = JSON.parse(rawDraft);
                }
            } catch {
                // Ignore storage/URL parse errors
            }
        }
        return {
            poNumber: queryPo || savedDraft?.poNumber || '',
            warehouseBookingId: queryWh || savedDraft?.warehouseBookingId || '', // Treated as shared booking ID
            carrierEmail: firstNonBlank(initialDefaults.carrierEmail, savedDraft?.carrierEmail),
        };
    };

    // Step 1: PO Validation
    const [poValidation, setPOValidation] = useState(getInitialPOValidation);
    const [validationResult, setValidationResult] = useState<POValidationResult | null>(() => initialStep2Session?.validationResult || null);

    // Step 2: Carrier Details
    const [carrierDetails, setCarrierDetails] = useState(() => ({
        mcNumber: firstNonBlank(initialStep2Session?.carrierDetails?.mcNumber, initialDefaults.mcNumber),
        carrierPhone: firstNonBlank(initialStep2Session?.carrierDetails?.carrierPhone, initialDefaults.carrierPhone),
        carrierName: firstNonBlank(initialStep2Session?.carrierDetails?.carrierName, initialDefaults.carrierName),
        driverName: initialStep2Session?.carrierDetails?.driverName || '',
    }));

    // Step 3: Confirmation
    const [confirmationCode, setConfirmationCode] = useState('');
    const [issuedClaimToken, setIssuedClaimToken] = useState('');
    const [emailNotificationStatus, setEmailNotificationStatus] = useState<'provider_accepted' | 'skipped' | 'failed' | 'unknown'>('unknown');
    const [submittedStatus, setSubmittedStatus] = useState<'pending' | 'approved'>('pending');
    const [assignedDockNumber, setAssignedDockNumber] = useState<string | null>(null);

    // Dynamic time slots and equipment/slot selection state from warehouse schedule
    const {
        timeSlots,
        setTimeSlots,
        timeSlotsLoading,
        setTimeSlotsLoading,
        equipmentDetails,
        setEquipmentDetails,
        invalidateTimeSlotsRequest,
        startTimeSlotsRequest,
        isTimeSlotsRequestValid
    } = useCarrierRequestSlots();

    // Fetch time slots when date changes
    const fetchTimeSlots = async (date: string) => {
        const bookingId = poValidation.warehouseBookingId.trim();
        if (!bookingId || !date) return;
        
        const requestId = startTimeSlotsRequest();
        setTimeSlotsLoading(true);
        
        try {
            const res = await fetch(
                `${API_BASE_URL}/public/carrier/availability?publicBookingId=${encodeURIComponent(bookingId)}&date=${encodeURIComponent(date)}`
            );
            
            // Abort if out of order
            if (!isTimeSlotsRequestValid(requestId)) return;
            
            if (res.ok) {
                const data = await res.json();
                
                // Check again after awaiting json
                if (!isTimeSlotsRequestValid(requestId)) return;

                setTimeSlots(data.availableTimeSlots || []);
                if (data.availableTimeSlots?.length) {
                    // Pre-select first non-full slot
                    const firstOpen = data.availableTimeSlots.find((s: any) => !s.isFull) || data.availableTimeSlots[0];
                    setEquipmentDetails(prev => ({ 
                        ...prev, 
                        requestedTime: firstOpen && !firstOpen.isFull ? firstOpen.localTime : '', 
                        requestedUtc: firstOpen && !firstOpen.isFull ? firstOpen.startUtc : '' 
                    }));
                } else {
                    setEquipmentDetails(prev => ({ ...prev, requestedTime: '', requestedUtc: '' }));
                }
            } else {
                setTimeSlots([]);
                setEquipmentDetails(prev => ({ ...prev, requestedTime: '', requestedUtc: '' }));
            }
        } catch (err) {
            console.error('Failed to fetch time slots:', err);
            if (isTimeSlotsRequestValid(requestId)) {
                setTimeSlots([]);
                setEquipmentDetails(prev => ({ ...prev, requestedTime: '', requestedUtc: '' }));
            }
        } finally {
            if (isTimeSlotsRequestValid(requestId)) {
                setTimeSlotsLoading(false);
            }
        }
    };

    // Determine if we have an authenticated carrier with canonical identity
    const isAuthenticatedCarrier = !!(user?.role === 'carrier' && user?.mcNumber?.trim());
    const isMcLocked = isAuthenticatedCarrier && Boolean(carrierDetails.mcNumber.trim());

    // Auto-populate from Auth User > Profile > Empty on mount or session refresh.
    // If the active carrier account changes (login, logout, or account switch on a shared browser),
    // immediately replace fields with the new carrier's defaults (or blank on logout) so no data leaks.
    useEffect(() => {
        const defaults = getCarrierDefaults();
        const accountChanged = prevCarrierKeyRef.current !== activeCarrierKey;

        if (accountChanged) {
            prevCarrierKeyRef.current = activeCarrierKey;
            editedFieldsRef.current = {};
            setFieldErrors({});
            setPOValidation(prev => ({
                ...prev,
                carrierEmail: defaults.carrierEmail,
            }));
            setCarrierDetails({
                mcNumber: defaults.mcNumber,
                carrierPhone: defaults.carrierPhone,
                carrierName: defaults.carrierName,
                driverName: '',
            });
            return;
        }

        setPOValidation(prev => ({
            ...prev,
            carrierEmail: editedFieldsRef.current.carrierEmail
                ? prev.carrierEmail
                : firstNonBlank(prev.carrierEmail, defaults.carrierEmail),
        }));

        setCarrierDetails(prev => ({
            ...prev,
            mcNumber: editedFieldsRef.current.mcNumber
                ? prev.mcNumber
                : firstNonBlank(prev.mcNumber, defaults.mcNumber),
            carrierPhone: editedFieldsRef.current.carrierPhone
                ? prev.carrierPhone
                : firstNonBlank(prev.carrierPhone, defaults.carrierPhone),
            carrierName: editedFieldsRef.current.carrierName
                ? prev.carrierName
                : firstNonBlank(prev.carrierName, defaults.carrierName),
        }));
    }, [activeCarrierKey, user?.id, user?.role, user?.email, user?.mcNumber, user?.companyName, user?.phone]);

    // Restore Step 2 timeSlots and equipmentDetails from sessionStorage on mount if refreshing during Step 2
    useEffect(() => {
        if (initialStep2Session) {
            if (Array.isArray(initialStep2Session.timeSlots)) {
                setTimeSlots(initialStep2Session.timeSlots);
            }
            if (initialStep2Session.equipmentDetails) {
                setEquipmentDetails(prev => ({
                    ...prev,
                    ...initialStep2Session.equipmentDetails,
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset validation state when going back to step 1
    useEffect(() => {
        if (step === 1) {
            setValidationResult(null);
            setError('');
            if (typeof window !== 'undefined') {
                try {
                    sessionStorage.removeItem('baywhiz_carrier_step2_session_v1');
                } catch {
                    // Ignore
                }
            }
        }
    }, [step]);

    // Persist Step 1/2 shipment lookup draft and Step 2 session state so page refresh preserves progress
    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            if (step === 3) {
                localStorage.removeItem('baywhiz_carrier_request_draft_v1');
                sessionStorage.removeItem('baywhiz_carrier_step2_session_v1');
                return;
            }
            if (poValidation.poNumber.trim() || poValidation.warehouseBookingId.trim() || poValidation.carrierEmail.trim()) {
                localStorage.setItem(
                    'baywhiz_carrier_request_draft_v1',
                    JSON.stringify({
                        poNumber: poValidation.poNumber,
                        warehouseBookingId: poValidation.warehouseBookingId,
                        carrierEmail: poValidation.carrierEmail,
                    })
                );
            }
            if (step === 2 && validationResult) {
                sessionStorage.setItem(
                    'baywhiz_carrier_step2_session_v1',
                    JSON.stringify({
                        step: 2,
                        carrierKey: activeCarrierKey,
                        poValidation,
                        validationResult,
                        carrierDetails,
                        equipmentDetails,
                        timeSlots,
                    })
                );
            }
        } catch {
            // Ignore storage errors
        }
    }, [step, activeCarrierKey, poValidation, validationResult, carrierDetails, equipmentDetails, timeSlots]);

    const handlePOValidation = async (e: FormEvent) => {
        e.preventDefault();

        // Reset state
        setError('');
        setLoading(true);
        setValidationResult(null);

        try {
            // 1. Trim Inputs
            const poNum = poValidation.poNumber.trim();
            const bookingId = poValidation.warehouseBookingId.trim();
            const email = poValidation.carrierEmail.trim();

            if (!poNum) throw new Error("PO Number is required");
            if (!bookingId) throw new Error("Warehouse ID is required");
            if (!email) throw new Error("Carrier Email is required");

            // 2. Build Payload (STRICT CONSTRUCTION - NO SPREADING)
            const payload = {
                poNumber: poNum,
                warehouseBookingId: bookingId,
                publicBookingId: bookingId, // Canonical
                carrierEmail: email
            };

            // 3. Network Request via API Client
            const data = await api.validatePublicCarrierPO(payload);

            invalidateTimeSlotsRequest();

            setValidationResult(data);

            const defaultDate = data.availableDates?.[0] || '';
            setEquipmentDetails(prev => ({
                ...prev,
                requestedDate: defaultDate,
                requestedTime: '',
                requestedUtc: ''
            }));

            // Initialize time slots from validate-po response
            if (data.availableTimeSlots?.length) {
                setTimeSlots(data.availableTimeSlots);
                // Pre-select first non-full slot
                const firstOpen = data.availableTimeSlots.find((s: any) => !s.isFull) || data.availableTimeSlots[0];
                setEquipmentDetails(prev => ({
                    ...prev,
                    requestedDate: defaultDate,
                    requestedTime: firstOpen && !firstOpen.isFull ? firstOpen.localTime : '',
                    requestedUtc: firstOpen && !firstOpen.isFull ? firstOpen.startUtc : ''
                }));
            } else {
                setTimeSlots([]);
            }

            setFieldErrors({});
            setStep(2);

        } catch (err: any) {
            // Handle API Errors (which throw)
            const status = err.status || 500;

            console.error("Validation Error:", err);

            // Handle 409 duplicate appointment
            if (status === 409 && err.details && (err.details.scheduledAt || err.details.existingStatus)) {
                const conflictTz = err.details.warehouseTimeZone || validationResult?.warehouse?.timeZone || 'America/Vancouver';
                const scheduled = err.details.scheduledAt
                    ? formatDateInTimeZone(err.details.scheduledAt, conflictTz, true)
                    : 'N/A';
                setError(
                    `This PO already has an active appointment (Status: ${err.details.existingStatus || 'active'}, Scheduled: ${scheduled}). Please cancel the existing request before submitting a new one.`
                );
            } else {
                setError(err.details?.error || err.message || 'Validation failed');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCarrierDetailsSubmit = (e: FormEvent) => {
        e.preventDefault();
        setError('');

        const mcTrimmed = carrierDetails.mcNumber?.trim() || '';
        const companyTrimmed = carrierDetails.carrierName?.trim() || '';
        const phoneTrimmed = carrierDetails.carrierPhone?.trim() || '';
        const driverTrimmed = carrierDetails.driverName?.trim() || '';

        const missingErrors: Record<string, string> = {};
        if (!mcTrimmed) missingErrors.mcNumber = 'MC number is required';
        if (!companyTrimmed) missingErrors.carrierName = 'Company name is required';
        if (!phoneTrimmed) missingErrors.carrierPhone = 'Phone number is required';
        if (!driverTrimmed) missingErrors.driverName = 'Driver name is required';

        if (Object.keys(missingErrors).length > 0) {
            setFieldErrors(missingErrors);
            setError('Please fill in MC number, driver name, phone, and company name');
            return;
        }

        // Validate phone digits
        const phoneErr = validatePhone(phoneTrimmed);
        if (phoneErr) {
            setFieldErrors({ carrierPhone: phoneErr });
            setError(phoneErr);
            return;
        }
        setFieldErrors({});

        // Skip directly to final submit (steps 2+3 merged)
        handleFinalSubmit(e);
    };

    const handleFinalSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (submittingRef.current) return;
        submittingRef.current = true;
        setError('');
        setLoading(true);

        try {
            const poNum = poValidation.poNumber.trim();
            const bookingId = poValidation.warehouseBookingId.trim();
            const email = poValidation.carrierEmail.trim();

            const carrierNameTrimmed = carrierDetails.carrierName.trim();
            const mcTrimmed = carrierDetails.mcNumber.trim();
            const phoneTrimmed = carrierDetails.carrierPhone.trim();
            const driverTrimmed = carrierDetails.driverName.trim();

            // STRICT CONSTRUCTION - NO SPREADING
            const payload = {
                // PO Validation Fields
                poNumber: poNum,
                warehouseBookingId: bookingId,
                publicBookingId: bookingId, // Canonical
                carrierEmail: email,

                // Carrier Details Fields
                mcNumber: mcTrimmed,
                carrierPhone: phoneTrimmed,
                carrierName: carrierNameTrimmed,
                driverName: driverTrimmed,

                // Equipment Details Fields
                truckNumber: equipmentDetails.truckNumber.trim(),
                trailerNumber: equipmentDetails.trailerNumber.trim(),
                trailerType: equipmentDetails.trailerType,
                requestedDate: equipmentDetails.requestedDate,
                requestedTime: equipmentDetails.requestedTime,
                requestedUtc: equipmentDetails.requestedUtc,
                estimatedPalletCount: Number(equipmentDetails.estimatedPalletCount) || 0,
            };

            const data = await api.submitPublicCarrierRequest(payload);

            // Save signed guest booking claim token so signing into or creating a Carrier Portal account
            // on this browser automatically links the booking to the carrier's dashboard.
            if (typeof window !== 'undefined' && data?.claimToken) {
                try {
                    const existingClaimsRaw = localStorage.getItem('baywhiz_guest_booking_claims_v1');
                    const existingClaims = existingClaimsRaw ? JSON.parse(existingClaimsRaw) : [];
                    const nextClaims = Array.isArray(existingClaims)
                        ? [...existingClaims.filter((t: string) => t !== data.claimToken), data.claimToken].slice(-20)
                        : [data.claimToken];
                    localStorage.setItem('baywhiz_guest_booking_claims_v1', JSON.stringify(nextClaims));
                } catch {
                    // Ignore storage errors
                }
            }

            // Persist completed carrier profile locally ONLY for authenticated carrier sessions
            // (never write a shared 'default' profile on unauthenticated shared browsers).
            if (typeof window !== 'undefined' && user?.role === 'carrier' && activeCarrierEmail) {
                try {
                    const storageKey = `baywhiz_carrier_profile_v1:${activeCarrierEmail}`;
                    localStorage.setItem(
                        storageKey,
                        JSON.stringify({
                            carrierName: carrierNameTrimmed,
                            mcNumber: mcTrimmed,
                            phone: phoneTrimmed,
                            email: activeCarrierEmail,
                        })
                    );
                } catch {
                    // Ignore storage quota errors
                }
            }

            setCarrierDetails(prev => ({
                ...prev,
                mcNumber: mcTrimmed,
                carrierPhone: phoneTrimmed,
                carrierName: carrierNameTrimmed,
                driverName: driverTrimmed,
            }));

            setConfirmationCode(data.confirmationCode);
            setSubmittedStatus(data.appointment?.status === 'approved' ? 'approved' : 'pending');
            setAssignedDockNumber(data.appointment?.dockNumber || null);
            setIssuedClaimToken(typeof data.claimToken === 'string' ? data.claimToken : '');
            const resolvedEmailStatus: 'provider_accepted' | 'skipped' | 'failed' | 'unknown' =
                data.emailNotificationStatus === 'provider_accepted' ||
                data.emailNotificationStatus === 'skipped' ||
                data.emailNotificationStatus === 'failed' ||
                data.emailNotificationStatus === 'unknown'
                    ? data.emailNotificationStatus
                    : data.emailNotificationSent === true
                        ? 'provider_accepted'
                        : data.emailNotificationSent === false
                            ? 'skipped'
                            : 'unknown';
            setEmailNotificationStatus(resolvedEmailStatus);
            setStep(3);
        } catch (err: any) {
            console.error("Backend Error:", err);
            if (err.status === 409 && err.details && (err.details.scheduledAt || err.details.existingStatus)) {
                const conflictTz = err.details.warehouseTimeZone || validationResult?.warehouse?.timeZone || 'America/Vancouver';
                const scheduled = err.details.scheduledAt
                    ? formatDateInTimeZone(err.details.scheduledAt, conflictTz, true)
                    : 'N/A';
                setError(
                    `This PO already has an active appointment (Status: ${err.details.existingStatus || 'active'}, Scheduled: ${scheduled}). Please cancel the existing request before submitting a new one.`
                );
                invalidateTimeSlotsRequest();
                setEquipmentDetails(prev => ({
                    ...prev,
                    requestedDate: '' // requestedTime and requestedUtc handled by invalidateTimeSlotsRequest
                }));
                setStep(1); // Block: send carrier back to Step 1
            } else if (err.status === 409) {
                setError(err.details?.error || err.message || 'This PO already has an active appointment.');
                invalidateTimeSlotsRequest();
                setEquipmentDetails(prev => ({
                    ...prev,
                    requestedDate: ''
                }));
                setStep(1);
            } else {
                setError(err.message || 'Submission failed');
            }
        } finally {
            submittingRef.current = false;
            setLoading(false);
        }
    };

    return (
        <div className="carrier-request-page min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
            <div className="max-w-2xl w-full">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-white mb-2">📦 BayWhiz Scheduling</h1>
                    <p className="text-slate-300">Request a dock appointment in under 2 minutes</p>
                </div>

                {/* Progress Indicator */}
                <div className="flex justify-between mb-8">
                    {[1, 2, 3].map((s) => (
                        <div key={s} className="flex items-center flex-1">
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${s === step
                                    ? 'bg-primary text-white scale-110'
                                    : s < step
                                        ? 'bg-green-500 text-white'
                                        : 'bg-slate-700 text-slate-400'
                                    }`}
                            >
                                {s < step ? '✓' : s}
                            </div>
                            {s < 3 && (
                                <div
                                    className={`flex-1 h-1 mx-2 transition-all ${s < step ? 'bg-green-500' : 'bg-slate-700'
                                        }`}
                                />
                            )}
                        </div>
                    ))}
                </div>

                {/* Step Labels */}
                <div className="flex justify-between mb-4 px-1">
                    {['Look Up Shipment', 'Pick a Time', 'Confirmed'].map((label, i) => (
                        <span key={label} className={`text-xs font-medium ${
                            i + 1 <= step ? 'text-teal-300' : 'text-slate-500'
                        }`} style={{ flex: 1, textAlign: i === 0 ? 'left' : i === 2 ? 'right' : 'center' }}>
                            {label}
                        </span>
                    ))}
                </div>

                {/* Main Card */}
                <div className="carrier-request-card bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20">
                    {error && (
                        <div role="alert" className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-100 break-words font-mono text-sm">
                            ⚠️ {error}
                        </div>
                    )}

                    {/* Step 1: PO Validation */}
                    {step === 1 && (
                        <>
                            <form onSubmit={handlePOValidation} className="space-y-6">
                                <div>
                                    <h2 className="text-2xl font-bold text-white mb-4">Look Up Your Shipment</h2>
                                    <p className="text-slate-300 mb-6">
                                        Enter the PO or Load number from your dispatch sheet.
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-slate-200 font-semibold mb-2">
                                        PO or Load Number *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={poValidation.poNumber}
                                        onChange={(e) =>
                                            setPOValidation({ ...poValidation, poNumber: e.target.value })
                                        }
                                        className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono"
                                        placeholder="e.g. PO-50 or LD-00003"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-200 font-semibold mb-2">
                                        Warehouse ID *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={poValidation.warehouseBookingId}
                                        onChange={(e) =>
                                            setPOValidation({ ...poValidation, warehouseBookingId: e.target.value })
                                        }
                                        className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono"
                                        placeholder="e.g. abbotsford-dc"
                                    />
                                    <p className="text-xs text-slate-400 mt-1">
                                        Enter the Warehouse ID provided by the facility (not the facility name).
                                    </p>
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-blue-100 font-semibold">
                                            Your Email *
                                        </label>
                                        <Link
                                            href="/carrier-settings"
                                            className="text-xs text-teal-400/80 hover:text-white underline cursor-pointer"
                                        >
                                            Edit carrier profile
                                        </Link>
                                    </div>
                                    <input
                                        type="email"
                                        required
                                        value={poValidation.carrierEmail}
                                        onChange={(e) => {
                                            editedFieldsRef.current.carrierEmail = true;
                                            setPOValidation({ ...poValidation, carrierEmail: e.target.value });
                                        }}
                                        className="w-full px-4 py-3  rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        placeholder="dispatch@carrier.com"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full py-4 bg-primary text-white font-bold rounded-lg hover:bg-primary/90 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                                >
                                    {loading ? 'Validating...' : 'Validate PO →'}
                                </button>
                            </form>
                        </>
                    )
                    }

                    {/* Step 2: Carrier Details */}
                    {
                        step === 2 && validationResult && (
                            <form onSubmit={handleCarrierDetailsSubmit} className="space-y-6" noValidate>
                                <div>
                                    <h2 className="text-2xl font-bold text-white mb-2">Pick a Time</h2>

                                    {/* Facility Identity Block — compact */}
                                    <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 mb-4 flex items-center gap-3">
                                        <span className="text-lg">🏭</span>
                                        <div>
                                            <p className="text-white font-semibold text-sm leading-tight">
                                                {validationResult.warehouse?.name}
                                            </p>
                                            <p className="text-slate-300 text-xs">
                                                {validationResult.load
                                                    ? `Load: ${validationResult.load.loadNumber}`
                                                    : `PO: ${validationResult.po?.number}`
                                                }
                                                {validationResult.linkedPOs && validationResult.linkedPOs.length > 1 && (
                                                    <> + {validationResult.linkedPOs.length - 1} more PO{validationResult.linkedPOs.length > 2 ? 's' : ''}</>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Date & Time — PROMOTED TO TOP */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="carrier-request-date" className="block text-slate-200 font-semibold mb-2">
                                            Date *
                                        </label>
                                        <select
                                            id="carrier-request-date"
                                            required
                                            value={equipmentDetails.requestedDate}
                                            onChange={(e) => {
                                                const newDate = e.target.value;
                                                invalidateTimeSlotsRequest();
                                                setEquipmentDetails(prev => ({
                                                    ...prev,
                                                    requestedDate: newDate,
                                                }));
                                                if (newDate) fetchTimeSlots(newDate);
                                            }}
                                            className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        >
                                            <option style={optionStyle} value="">Select a date...</option>
                                            {(validationResult.availableDateOptions || validationResult.availableDates?.map(d => ({ value: d, label: d })) || []).map((opt) => (
                                                <option style={optionStyle} key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label htmlFor="carrier-request-time" className="block text-slate-200 font-semibold mb-2">
                                            Time *
                                        </label>
                                        <select
                                            id="carrier-request-time"
                                            required
                                            value={equipmentDetails.requestedUtc || ''}
                                            onChange={(e) => {
                                                const selectedValue = e.target.value;
                                                const slot = timeSlots.find(s => s.startUtc === selectedValue);
                                                
                                                if (slot) {
                                                    setEquipmentDetails({
                                                        ...equipmentDetails,
                                                        requestedTime: slot.localTime,
                                                        requestedUtc: slot.startUtc,
                                                    });
                                                } else {
                                                    setEquipmentDetails({
                                                        ...equipmentDetails,
                                                        requestedTime: '',
                                                        requestedUtc: '',
                                                    });
                                                }
                                            }}
                                            className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        >
                                            <option style={optionStyle} value="">Select a time...</option>
                                            {timeSlotsLoading ? (
                                                <option style={optionStyle} disabled>Loading...</option>
                                            ) : timeSlots.length > 0 ? (
                                                timeSlots.map((slot) => {
                                                    const capSuffix = typeof slot.availableDocks === 'number' && typeof slot.totalDocks === 'number' && slot.totalDocks > 0
                                                        ? slot.isFull
                                                            ? ` (Full ? 0 of ${slot.totalDocks} docks open)`
                                                            : slot.availableDocks < slot.totalDocks
                                                                ? ` (${slot.availableDocks} of ${slot.totalDocks} docks open)`
                                                                : ` (Open ? ${slot.availableDocks} of ${slot.totalDocks} docks)`
                                                        : '';
                                                    return (
                                                        <option style={optionStyle} key={slot.startUtc} value={slot.startUtc} disabled={Boolean(slot.isFull)}>
                                                            {slot.displayLabel}{capSuffix}
                                                        </option>
                                                    );
                                                })
                                            ) : (
                                                <option style={optionStyle} disabled>Select a date first</option>
                                            )}
                                        </select>
                                    </div>
                                </div>

                                {/* MC Number — always visible; locked only when authenticated carrier has a non-empty MC number */}
                                <div>
                                    <label htmlFor="carrier-mc-number" className="block text-slate-200 font-semibold mb-2">
                                        MC Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="carrier-mc-number"
                                        type="text"
                                        required
                                        value={carrierDetails.mcNumber}
                                        onChange={(e) => {
                                            if (!isMcLocked) {
                                                editedFieldsRef.current.mcNumber = true;
                                                setCarrierDetails({ ...carrierDetails, mcNumber: e.target.value });
                                                if (fieldErrors.mcNumber) {
                                                    setFieldErrors(prev => ({ ...prev, mcNumber: '' }));
                                                }
                                            }
                                        }}
                                        readOnly={isMcLocked}
                                        className={`w-full px-4 py-3 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                                            fieldErrors.mcNumber
                                                ? 'bg-red-500/10 border-red-500'
                                                : isMcLocked
                                                    ? 'bg-white/5 border-white/10 cursor-not-allowed opacity-75'
                                                    : 'bg-white/10 border-white/20 placeholder-white/40'
                                        }`}
                                        placeholder="MC123456"
                                    />
                                    {fieldErrors.mcNumber ? (
                                        <p className="text-xs text-red-300 mt-1">{fieldErrors.mcNumber}</p>
                                    ) : (
                                        <p className="text-xs text-slate-400/60 mt-1">
                                            {isMcLocked
                                                ? 'Prefilled from your account'
                                                : 'Used to match your company to past bookings'}
                                        </p>
                                    )}
                                </div>

                                {/* Company Name & Phone Number — always visible and editable so incomplete profiles never block booking */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="carrier-company-name" className="block text-slate-200 font-semibold mb-2">
                                            Company Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="carrier-company-name"
                                            type="text"
                                            required
                                            value={carrierDetails.carrierName}
                                            onChange={(e) => {
                                                editedFieldsRef.current.carrierName = true;
                                                setCarrierDetails({ ...carrierDetails, carrierName: e.target.value });
                                                if (fieldErrors.carrierName) {
                                                    setFieldErrors(prev => ({ ...prev, carrierName: '' }));
                                                }
                                            }}
                                            className={`w-full px-4 py-3 rounded-lg border text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                                                fieldErrors.carrierName ? 'bg-red-500/10 border-red-500' : 'bg-white/10 border-white/20'
                                            }`}
                                            placeholder="ABC Logistics Inc."
                                        />
                                        {fieldErrors.carrierName && (
                                            <p className="text-xs text-red-300 mt-1">{fieldErrors.carrierName}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label htmlFor="carrier-phone-number" className="block text-slate-200 font-semibold mb-2">
                                            Phone Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="carrier-phone-number"
                                            type="tel"
                                            required
                                            value={carrierDetails.carrierPhone}
                                            onChange={(e) => {
                                                editedFieldsRef.current.carrierPhone = true;
                                                setCarrierDetails({ ...carrierDetails, carrierPhone: e.target.value });
                                                if (fieldErrors.carrierPhone) {
                                                    setFieldErrors(prev => ({ ...prev, carrierPhone: '' }));
                                                }
                                            }}
                                            className={`w-full px-4 py-3 rounded-lg border text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                                                fieldErrors.carrierPhone ? 'bg-red-500/10 border-red-500' : 'bg-white/10 border-white/20'
                                            }`}
                                            placeholder="604-555-0100"
                                        />
                                        {fieldErrors.carrierPhone && (
                                            <p className="text-xs text-red-300 mt-1">{fieldErrors.carrierPhone}</p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="carrier-driver-name" className="block text-slate-200 font-semibold mb-2">
                                        Driver Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="carrier-driver-name"
                                        type="text"
                                        required
                                        value={carrierDetails.driverName}
                                        onChange={(e) => {
                                            setCarrierDetails({ ...carrierDetails, driverName: e.target.value });
                                            if (fieldErrors.driverName) {
                                                setFieldErrors(prev => ({ ...prev, driverName: '' }));
                                            }
                                        }}
                                        className={`w-full px-4 py-3 rounded-lg border text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                                            fieldErrors.driverName ? 'bg-red-500/10 border-red-500' : 'bg-white/10 border-white/20'
                                        }`}
                                        placeholder="Driver's name"
                                    />
                                    {fieldErrors.driverName && (
                                        <p className="text-xs text-red-300 mt-1">{fieldErrors.driverName}</p>
                                    )}
                                </div>

                                {/* Equipment Details — optional, collapsible */}
                                <div>
                                    <button
                                        type="button"
                                        onClick={() => setEquipmentExpanded(!equipmentExpanded)}
                                        className="flex items-center gap-2 text-sm text-teal-400/80 hover:text-white transition-colors mb-3"
                                    >
                                        <span>{equipmentExpanded ? '▾' : '▸'}</span>
                                        Equipment details
                                        <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded-full text-slate-400">Optional</span>
                                    </button>
                                    {equipmentExpanded && (
                                        <div className="space-y-4 pl-4 border-l-2 border-white/10">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-slate-200 text-sm mb-1">Truck #</label>
                                                    <input
                                                        type="text"
                                                        value={equipmentDetails.truckNumber}
                                                        onChange={(e) =>
                                                            setEquipmentDetails({ ...equipmentDetails, truckNumber: e.target.value })
                                                        }
                                                        className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                        placeholder="e.g. TRK-001"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-slate-200 text-sm mb-1">Trailer #</label>
                                                    <input
                                                        type="text"
                                                        value={equipmentDetails.trailerNumber}
                                                        onChange={(e) =>
                                                            setEquipmentDetails({ ...equipmentDetails, trailerNumber: e.target.value })
                                                        }
                                                        className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                        placeholder="e.g. TRL-002"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-slate-200 text-sm mb-1">Trailer Type</label>
                                                <select
                                                    value={equipmentDetails.trailerType}
                                                    onChange={(e) =>
                                                        setEquipmentDetails({ ...equipmentDetails, trailerType: e.target.value as any })
                                                    }
                                                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                >
                                                    <option style={optionStyle} value="Van">Dry Van</option>
                                                    <option style={optionStyle} value="Reefer">Reefer</option>
                                                    <option style={optionStyle} value="Flatbed">Flatbed</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-slate-200 text-sm mb-1">Est. Pallet Count</label>
                                                <input
                                                    type="number"
                                                    value={equipmentDetails.estimatedPalletCount || ''}
                                                    onChange={(e) =>
                                                        setEquipmentDetails({ ...equipmentDetails, estimatedPalletCount: parseInt(e.target.value) || 0 })
                                                    }
                                                    min="0"
                                                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                    placeholder="0"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-4">
                                    <p className="text-yellow-100 font-semibold">⚠️ This is a request, not a confirmed booking</p>
                                    <p className="text-yellow-200 text-sm mt-1">
                                        You'll receive dock assignment via email once the warehouse approves.
                                    </p>
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            invalidateTimeSlotsRequest();
                                            setStep(1);
                                        }}
                                        className="flex-1 py-4 bg-slate-700 text-white font-bold rounded-lg hover:bg-slate-600 transition-all"
                                    >
                                        ← Back
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading || !equipmentDetails.requestedDate || !equipmentDetails.requestedTime}
                                        className="flex-1 py-4 bg-primary text-white font-bold rounded-lg hover:bg-primary/90 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                                    >
                                        {loading ? 'Submitting...' : 'Submit Request →'}
                                    </button>
                                </div>
                            </form>
                        )
                    }

                    {/* Step 2 merged flow now handles final submit directly — Step 3 (old Step 4) is just confirmation */}

                    {/* Step 3: Confirmation */}
                    {
                        step === 3 && (
                            <div className="space-y-6">
                                {/* Success Header */}
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <span className="text-3xl">✓</span>
                                    </div>
                                    <h2 className="text-2xl font-bold text-white mb-1">Request Submitted</h2>
                                    <p className="text-slate-300 text-sm">Your appointment request has been sent for review.</p>
                                </div>

                                {/* Warehouse Identity */}
                                {validationResult?.warehouse && (
                                    <div className="bg-white/10 rounded-xl p-5 border border-white/15 space-y-3">
                                        <p className="text-slate-300 text-xs font-semibold uppercase tracking-wider">Submitted to</p>
                                        <div className="space-y-1">
                                            <p className="text-white text-lg font-bold leading-tight">{validationResult.warehouse.name}</p>
                                            {validationResult.warehouse.address && (
                                                <p className="text-slate-300 text-sm">{validationResult.warehouse.address}</p>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-white/10">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-slate-400 text-xs">Warehouse ID:</span>
                                                <span className="font-mono text-white text-xs bg-white/10 px-2 py-0.5 rounded">{validationResult.warehouse.publicBookingId}</span>
                                            </div>
                                            {validationResult.warehouse.timeZone && (
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-slate-400 text-xs">Timezone:</span>
                                                    <span className="text-white text-xs">{validationResult.warehouse.timeZone.replace(/_/g, ' ')}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Confirmation Details */}
                                <div className="bg-primary/20 border border-primary/30 rounded-xl p-5 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <p className="text-slate-300 text-xs font-semibold uppercase tracking-wider">Confirmation</p>
                                        <span className="font-mono text-white text-base bg-primary/30 px-3 py-1 rounded-lg border border-primary/40">{confirmationCode}</span>
                                    </div>
                                    {issuedClaimToken && (
                                        <div className="pt-2 border-t border-white/10 flex items-center justify-between gap-2 flex-wrap">
                                            <span className="text-slate-300 text-xs">
                                                Signed Claim Token (auto-saved in this browser for Carrier Portal linking)
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (navigator?.clipboard) {
                                                        navigator.clipboard.writeText(issuedClaimToken);
                                                    }
                                                }}
                                                className="text-xs font-mono bg-white/15 hover:bg-white/25 text-white px-2.5 py-1 rounded border border-white/20 transition-colors"
                                            >
                                                Copy Claim Token
                                            </button>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/10">
                                        <div>
                                            <p className="text-slate-400 text-xs mb-0.5">Status</p>
                                            {submittedStatus === 'approved' ? (
                                                <p className="text-emerald-300 text-sm font-semibold">
                                                    {assignedDockNumber ? `Approved ? Assigned to Dock ${assignedDockNumber}` : 'Approved'}
                                                </p>
                                            ) : (
                                                <p className="text-yellow-300 text-sm font-semibold">Awaiting Approval</p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-slate-400 text-xs mb-0.5">Requested Time</p>
                                            <p className="text-white text-sm font-semibold">
                                                {equipmentDetails.requestedDate ? new Date(equipmentDetails.requestedDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '--'} · {equipmentDetails.requestedTime || '--'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <p className="text-slate-400 text-xs mb-0.5">PO / Load</p>
                                            <p className="text-white text-sm font-mono">{poValidation.poNumber}</p>
                                        </div>
                                        <div>
                                            <p className="text-slate-400 text-xs mb-0.5">Carrier</p>
                                            <p className="text-white text-sm">{carrierDetails.carrierName || '--'}</p>
                                        </div>
                                    </div>

                                    {/* Load context — show included POs for load-based bookings */}
                                    {validationResult?.load && (
                                        <div className="pt-2 border-t border-white/10">
                                            <p className="text-slate-300 text-sm">
                                                <strong>Load:</strong>{' '}
                                                <span className="font-mono text-teal-300">{validationResult.load.loadNumber}</span>
                                            </p>
                                            {validationResult.linkedPOs && validationResult.linkedPOs.length > 0 && (
                                                <div className="mt-2">
                                                    <p className="text-slate-300 text-xs font-semibold mb-1">
                                                        {validationResult.linkedPOs.length} PO{validationResult.linkedPOs.length !== 1 ? 's' : ''} included:
                                                    </p>
                                                    <div className="flex flex-wrap gap-1.5 mt-1">
                                                        {validationResult.linkedPOs.map((po) => (
                                                            <span key={po.id} className="text-xs font-mono bg-white/10 text-blue-100 px-2 py-0.5 rounded border border-white/10">
                                                                {po.number}{po.skids ? ` (${po.skids} plt)` : ''}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    {validationResult.load.excludedPOs != null && validationResult.load.excludedPOs > 0 && (
                                                        <p className="text-amber-300 text-xs mt-2">
                                                            ⚠ {validationResult.load.excludedPOs} linked PO{validationResult.load.excludedPOs !== 1 ? 's were' : ' was'} not open and {validationResult.load.excludedPOs !== 1 ? 'were' : 'was'} excluded
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* What Happens Next */}
                                <div className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-3">
                                    <p className="text-white text-sm font-semibold">What happens next?</p>
                                    <ul className="space-y-2 text-slate-300 text-sm">
                                        <li className="flex items-start gap-2">
                                            <span className="text-teal-400 mt-0.5 shrink-0">1.</span>
                                            The warehouse will review your request
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-teal-400 mt-0.5 shrink-0">2.</span>
                                            You'll be notified when approved or if changes are needed
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-teal-400 mt-0.5 shrink-0">3.</span>
                                            Arrive close to your approved appointment time
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-teal-400 mt-0.5 shrink-0">4.</span>
                                            Use BayWhiz check-in when arriving onsite
                                        </li>
                                    </ul>
                                    <p className="text-slate-400/60 text-xs pt-1">
                                        The warehouse may approve, revise, or decline your requested time.
                                    </p>
                                </div>

                                {/* Arrival Instructions */}
                                <div className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-3">
                                    <p className="text-white text-sm font-semibold">Arrival & Check-In</p>
                                    <ul className="space-y-1.5 text-slate-300 text-sm">
                                        <li className="flex items-start gap-2">
                                            <span className="text-emerald-400 shrink-0">✓</span>
                                            Check in through BayWhiz when you arrive onsite
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-emerald-400 shrink-0">✓</span>
                                            Follow facility signage and dock instructions
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-emerald-400 shrink-0">✓</span>
                                            Wait for dock assignment if not yet assigned
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-emerald-400 shrink-0">✓</span>
                                            Contact the warehouse if you'll be delayed
                                        </li>
                                    </ul>
                                    {validationResult?.warehouse?.phone && (
                                        <p className="text-slate-400 text-xs pt-1">
                                            Warehouse contact: <span className="text-white font-mono">{validationResult.warehouse.phone}</span>
                                        </p>
                                    )}
                                </div>

                                {/* Email / Notification Status Notice */}
                                <div className="bg-green-500/15 border border-green-500/30 rounded-xl p-4 flex items-start gap-3">
                                    <span className="text-lg shrink-0">
                                        {emailNotificationStatus === 'provider_accepted' ? '📧' : '🔖'}
                                    </span>
                                    <p className="text-green-100 text-sm">
                                        {emailNotificationStatus === 'provider_accepted'
                                            ? "Your booking request has been received and a confirmation email was accepted by our email provider. You'll also receive updates when the warehouse approves or revises your request. Save your confirmation code for reference."
                                            : emailNotificationStatus === 'failed'
                                                ? "Your booking request has been received and saved, but the confirmation email could not be sent right now. Please save your confirmation code for reference and track status updates in your Carrier Portal."
                                                : emailNotificationStatus === 'skipped'
                                                    ? "Your booking request has been received and saved (email notification was skipped for this request). Save your confirmation code for reference and track approval and status updates directly in your Carrier Portal."
                                                    : "Your booking request has been received and saved. Email delivery status could not be confirmed immediately — please save your confirmation code for reference and check your Carrier Portal for status updates."}
                                    </p>
                                </div>

                                <div className="space-y-3">
                                    <button
                                        onClick={() => {
                                            const defaults = getCarrierDefaults();
                                            editedFieldsRef.current = {};
                                            setFieldErrors({});
                                            invalidateTimeSlotsRequest();
                                            setStep(1);
                                            setPOValidation({
                                                poNumber: '',
                                                warehouseBookingId: '',
                                                carrierEmail: defaults.carrierEmail,
                                            });
                                            setCarrierDetails({
                                                mcNumber: defaults.mcNumber,
                                                carrierPhone: defaults.carrierPhone,
                                                carrierName: defaults.carrierName,
                                                driverName: '',
                                            });
                                            setEquipmentDetails({
                                                truckNumber: '',
                                                trailerNumber: '',
                                                trailerType: 'Van',
                                                requestedDate: '',
                                                requestedTime: '',
                                                requestedUtc: '',
                                                estimatedPalletCount: 0,
                                            });
                                            setConfirmationCode('');
                                            setSubmittedStatus('pending');
                                            setAssignedDockNumber(null);
                                            setEmailNotificationStatus('unknown');
                                            setValidationResult(null);
                                        }}
                                        className="w-full py-4 bg-primary text-white font-bold rounded-lg hover:bg-primary/90 transition-all transform hover:scale-[1.02] shadow-lg"
                                    >
                                        Submit Another Request
                                    </button>
                                    {user?.role === 'carrier' ? (
                                        <Link
                                            href="/carrier-dashboard"
                                            className="block w-full py-3 text-center text-slate-300 hover:text-white text-sm font-medium transition-colors"
                                        >
                                            ← Back to Your Appointments
                                        </Link>
                                    ) : (
                                        <div className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-3 text-center">
                                            <p className="text-white text-sm font-semibold">
                                                Track or manage this booking in the Carrier Portal
                                            </p>
                                            <p className="text-slate-300 text-xs">
                                                Sign in or create a free Carrier Portal account on this browser and this booking will link to your dashboard automatically.
                                            </p>
                                            <div className="flex flex-col sm:flex-row gap-2 justify-center">
                                                <Link
                                                    href="/carrier-login?from=/carrier-dashboard"
                                                    className="flex-1 py-2.5 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
                                                >
                                                    Carrier Sign In
                                                </Link>
                                                <Link
                                                    href="/carrier-signup?from=/carrier-dashboard"
                                                    className="flex-1 py-2.5 px-4 rounded-lg bg-teal-600/80 hover:bg-teal-600 text-white text-xs font-semibold transition-colors"
                                                >
                                                    Create Free Carrier Account
                                                </Link>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    }
                </div >

                <p className="text-center text-slate-400 text-sm mt-6">
                    Powered by BayWhiz Logistics © 2026
                </p>
            </div >
        </div >
    );
}
